import { useQuery } from '@tanstack/react-query';
import type { CSSProperties } from 'react';
import { useParams } from 'react-router-dom';

import { globeImage } from '@revelio/assets';
import { NoData } from '@revelio/replots';
import { Divider, Spinner, Text } from '@revelio/ui';

import rlLogo from '../../../../images/rl-logo.svg';
import {
  getSharedReport,
  type SharedReportSnapshot,
} from '../../api/shared-reports-api';
import { TextChartContent } from '../Chart/TextChart/TextChartContent';
import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
  REPORT_PAGE_HEIGHT,
  REPORT_PAGE_WIDTH,
  REPORT_THEME,
} from '../PageConfigProvider/constants';
import type { TabContent } from '../PageConfigProvider/page-reducer';
import type { Page } from '../PageConfigProvider/types';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import { ReportFooterPage } from '../Reports/ReportFooterPage';
import {
  SHARED_FLOW_LAYOUT_MARKER,
  SharedFlowReport,
} from './SharedFlowReport';
import type { ChartDataEntry } from './SharedReportDataContext';
import { StaticChartRenderer } from './StaticChartRenderer';
import './shared-report.css';

import styles from './shared-report-viewer.module.css';

interface CoverParameter {
  label: string;
  value: string;
}

const halfWidth = REPORT_PAGE_WIDTH / 2;
const reportCenterPadding = `max(calc(50% - ${halfWidth}px), ${REPORT_THEME.margins.minCenterMargin}px)`;

const StaticCoverPage = ({
  title,
  description,
  parameters,
}: {
  title: string;
  description: string;
  parameters: CoverParameter[];
}) => {
  const topPercent = (8 / DEFAULT_GRID_ROWS) * 100;

  const mid = Math.ceil(parameters.length / 2);
  const leftColumn = parameters.slice(0, mid);
  const rightColumn = parameters.slice(mid);

  return (
    <div
      className={styles.coverPage}
      style={
        {
          '--report-center-padding': reportCenterPadding,
          '--report-cover-logo-top': `${REPORT_THEME.margins.pdf}px`,
          '--report-cover-content-top': `${topPercent}%`,
        } as CSSProperties
      }
    >
      <img
        className={styles.globeImage}
        src={globeImage}
        alt="Report Cover"
        loading="eager"
      />
      <img
        className={styles.logo}
        src={rlLogo}
        alt="Revelio Labs logo"
        loading="eager"
      />
      <div className={styles.coverContent}>
        <Text
          as="h1"
          fontSize="x-lg"
          weight="bold"
          tone="inherit"
          className={styles.coverTitle}
        >
          {title || 'Title'}
        </Text>
        <Text
          fontSize="md"
          className={styles.coverDescription}
          data-titled={title ? true : undefined}
        >
          {description || 'Description'}
        </Text>
        {parameters.length > 0 && (
          <div className={styles.parameterColumns}>
            <div className={styles.parameterColumn}>
              {leftColumn.map((param) => (
                <div
                  key={param.label}
                  className={styles.leftParameterItem}
                  data-spaced={rightColumn.length > 0 || undefined}
                >
                  <Text
                    fontSize="sm"
                    weight="bold"
                    className={styles.parameterLabel}
                  >
                    {param.label}
                  </Text>
                  <Text fontSize="sm" className={styles.parameterValue}>
                    {param.value}
                  </Text>
                </div>
              ))}
            </div>
            {rightColumn.length > 0 && (
              <>
                <Divider className={styles.columnRule} orientation="vertical" />
                <div className={styles.parameterColumn}>
                  {rightColumn.map((param) => (
                    <div className={styles.parameterItem} key={param.label}>
                      <Text
                        fontSize="sm"
                        weight="bold"
                        className={styles.parameterLabel}
                      >
                        {param.label}
                      </Text>
                      <Text fontSize="sm" className={styles.parameterValue}>
                        {param.value}
                      </Text>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface SharedReportContentProps {
  snapshot: SharedReportSnapshot;
}

const SharedReportContent = ({ snapshot }: SharedReportContentProps) => {
  const pageConfig = snapshot.pageConfig as {
    name: string;
    _layout?: string;
    _resolvedCoverParameters?: CoverParameter[];
    tabs?: Array<{
      tabContent?: {
        items?: PositionedItem[];
        grid?: { rows?: number; columns?: number };
        title?: string;
        description?: string;
      };
    }>;
  };

  const tabContent = pageConfig.tabs?.[0]?.tabContent;
  const items = tabContent?.items ?? [];
  const gridRows = tabContent?.grid?.rows ?? DEFAULT_GRID_ROWS;
  const gridColumns = tabContent?.grid?.columns ?? DEFAULT_GRID_COLUMNS;
  const chartData = snapshot.chartData as Record<string, ChartDataEntry>;

  if (pageConfig._layout === SHARED_FLOW_LAYOUT_MARKER && tabContent) {
    return (
      <SharedFlowReport
        page={pageConfig as unknown as Page}
        tabContent={{ ...tabContent, items } as TabContent}
        parameters={pageConfig._resolvedCoverParameters ?? []}
        chartData={chartData}
      />
    );
  }

  const numPages = Math.max(1, Math.ceil(gridRows / DEFAULT_GRID_ROWS));
  const totalHeight = numPages * REPORT_PAGE_HEIGHT;

  const pageDividers = Array.from({ length: numPages }).map((_, index) => (
    <Divider
      key={`divider-${index}`}
      className={styles.pageDivider}
      style={
        {
          '--page-divider-top': `${(index + 1) * REPORT_PAGE_HEIGHT}px`,
        } as CSSProperties
      }
    />
  ));

  return (
    <div
      className={styles.report}
      style={
        {
          '--shared-report-height': `${totalHeight}px`,
          '--shared-report-page-height': `${REPORT_PAGE_HEIGHT}px`,
          '--shared-report-footer-top': `${(numPages - 1) * REPORT_PAGE_HEIGHT}px`,
          '--report-center-padding': reportCenterPadding,
          '--shared-report-grid-rows': `repeat(${gridRows}, 1fr)`,
          '--shared-report-grid-columns': `repeat(${gridColumns}, 1fr)`,
        } as CSSProperties
      }
    >
      {/* Cover page */}
      <div className={styles.coverPageSlot}>
        <StaticCoverPage
          title={tabContent?.title ?? pageConfig.name ?? ''}
          description={tabContent?.description ?? ''}
          parameters={pageConfig._resolvedCoverParameters ?? []}
        />
      </div>

      {/* Footer page */}
      <div className={styles.footerPageSlot}>
        <ReportFooterPage />
      </div>

      {/* Chart grid */}
      <div className={styles.chartGrid}>
        {items.map((item) => {
          const entry = chartData[item.id];

          const itemStyle = {
            '--item-grid-column': `${item.position.x + 1} / span ${item.position.width}`,
            '--item-grid-row': `${item.position.y + 1} / span ${item.position.height}`,
          } as CSSProperties;

          if (item.plot?.chartType === 'text') {
            return (
              <div key={item.id} className={styles.gridItem} style={itemStyle}>
                <div className={styles.card}>
                  <div className={styles.textCardBody}>
                    <TextChartContent
                      content={item.plot?.textHtml ?? ''}
                      color={item.plot?.color}
                      vAlign={item.plot?.textVAlign}
                    />
                  </div>
                </div>
              </div>
            );
          }

          if (!entry?.component) {
            return (
              <div key={item.id} className={styles.gridItem} style={itemStyle}>
                <div className={styles.card}>
                  <div className={styles.emptyCardBody}>
                    <NoData />
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div
              key={item.id}
              className={styles.gridItem}
              style={itemStyle}
              data-raised={item.plot?.color === '#ffffff' || undefined}
            >
              <div className={styles.card}>
                {item.title && (
                  <div className={styles.cardHeader}>
                    <Text fontSize="sm" weight="semibold" lineClamp={1}>
                      {item.title}
                    </Text>
                  </div>
                )}
                <div
                  className={styles.chartCardBody}
                  data-titled={item.title ? true : undefined}
                >
                  <StaticChartRenderer chartDataEntry={entry} />
                </div>
              </div>
            </div>
          );
        })}
        {pageDividers}
      </div>
    </div>
  );
};

export const SharedReportViewer = () => {
  const { token } = useParams<{ token: string }>();

  const {
    data: snapshot,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['shared-report', token],
    queryFn: () => getSharedReport(token ?? ''),
    enabled: !!token,
    retry: 1,
    staleTime: Infinity,
  });

  if (isLoading) {
    return (
      <div className={styles.statusPage}>
        <div className={styles.statusContent}>
          <Spinner size="xl" />
          <Text className={styles.statusText}>Loading shared report...</Text>
        </div>
      </div>
    );
  }

  if (error || !snapshot) {
    return (
      <div className={styles.statusPage}>
        <div className={styles.statusContent}>
          <Text
            as="h2"
            fontSize="bg"
            weight="bold"
            className={styles.statusTitle}
          >
            Report not found
          </Text>
          <Text className={styles.statusMessage}>
            This shared report link may be invalid or the report may have been
            removed.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="shared-report-page">
      <SharedReportContent snapshot={snapshot} />
    </div>
  );
};
